import {
  toWSDateString,
  useWSModalCustom,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInfoBox,
  WSInputDate,
  WSList,
  WSModal,
  WSPill,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { DataItem } from "../../../../shared/components/DataItem";
import { useGetNow } from "../../../../shared/hooks/useGetNow";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { addDays, getStartOfDay } from "../../../../utils/dates";
import { queriesPayeeRows } from "../../../../v3/query/queriesPayeeRows";
import { queriesRequirements } from "../../../../v3/query/queriesRequirements";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { selectorIsRequirementWithApprovalFlow } from "../../selectors/selectorIsRequirementWithApprovalFlow";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";
import {
  expirationExtensionOptions,
  extendExpirationDefaultValues,
  ExtendExpirationFormData,
  getExtendExpirationDate,
  getExtendExpirationValidationSchema
} from "../ExtendExpirationDateModal/utils";

export interface MarkRequirementAsCompleteModalProps {
  onClose: () => void;
  requirement: PayeeRequirement;
  context: ContextItem;
  additionalData?: RequirementAdditionalData;
  currentStatus: RequirementStatusViewProps;
  onSuccess: () => void;
}

export const MarkRequirementAsCompleteModal: React.FC<
  MarkRequirementAsCompleteModalProps
> = ({ onClose, requirement, context, onSuccess, currentStatus }) => {
  const now = useGetNow();

  const [approveRequirement, approveRequirementMeta] =
    queriesRequirements.approvePayeeRequirement.useMutation();

  const [showExtendExpiration, setShowExtendExpiration] = React.useState(false);

  const xWingspanAccount = useXWingspanExpansion();
  const xWingspanPayerId = useXWingspanPayerId();
  const queryPayeeGeneralSummary = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        requirements_requirementId: { eq: requirement.id }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  const contractorsLength = queryPayeeGeneralSummary.data || "-";

  const isApprovalFlow = selectorIsRequirementWithApprovalFlow(requirement);

  return (
    <WSForm<ExtendExpirationFormData>
      defaultValues={extendExpirationDefaultValues}
      validationSchema={getExtendExpirationValidationSchema(
        showExtendExpiration ? requirement.expirationDate : undefined
      )}
      onSubmit={formData => {
        const nextDate = getExtendExpirationDate(
          formData.extensionType,
          formData.customDate
        );

        approveRequirement(
          {
            requirementId: requirement.id!,
            xWingspanAccount: xWingspanPayerId,
            ...(showExtendExpiration
              ? {
                  approvePayeeRequirementRequest: {
                    expiresAt: nextDate
                  }
                }
              : {})
          },
          {
            onSuccess() {
              onSuccess();
              onClose();
            }
          }
        );
      }}
    >
      {({ watch }) => {
        const extensionType = watch("extensionType");
        const customDate = watch("customDate");

        const nextDate = getExtendExpirationDate(extensionType, customDate);

        const isNextDateEarlier =
          nextDate && requirement.expiresAt && nextDate < requirement.expiresAt;
        const isNextDate90DaysLate = nextDate && nextDate > addDays(now, 90);

        return (
          <WSModal
            actions={{
              alignment: "fill",
              buttons: [
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: onClose,
                  type: "button"
                },
                {
                  kind: "Primary",
                  label: isApprovalFlow ? "Yes, approve" : "Yes, mark complete",
                  type: "submit",
                  loading: approveRequirementMeta.isLoading
                }
              ]
            }}
            title={
              isApprovalFlow
                ? "Approve requirement"
                : "Mark requirement as complete"
            }
            onClose={onClose}
          >
            <WSList gap="XL">
              <WSFlexBox gap="M" wrap="nowrap">
                <WSElement>
                  <WSText.ParagraphSm color="gray600" mb="S">
                    Current status:
                  </WSText.ParagraphSm>
                  <WSPill {...currentStatus.pill}></WSPill>
                  <WSText.ParagraphXs color="gray400" ml="S" mt="XS">
                    {currentStatus.secondaryText}
                  </WSText.ParagraphXs>
                </WSElement>
                <WSIcon
                  name="arrow-right"
                  style={{
                    marginTop: "auto",
                    marginBottom: "auto"
                  }}
                />

                <WSElement>
                  <WSText.ParagraphSm color="gray600" mb="S">
                    Overwritten status preview:
                  </WSText.ParagraphSm>
                  <WSPill icon text="Complete" theme="success"></WSPill>
                  <WSText.ParagraphXs color="gray400" ml="S" mt="XS">
                    {isApprovalFlow
                      ? "Approved by you"
                      : "Marked complete by you"}
                  </WSText.ParagraphXs>
                </WSElement>
              </WSFlexBox>
              {requirement.expiresAt ? (
                <WSInfoBox py="L">
                  <WSList gap="M">
                    <WSFlexBox gap="M" justify="space-between" wrap="nowrap">
                      <WSElement>
                        <WSText.ParagraphXs color="gray500" mb="S">
                          Current expiration date
                        </WSText.ParagraphXs>
                        <WSText.ParagraphSm color="gray600" mt="XS">
                          {toWSDateString(requirement.expiresAt, "input")}
                        </WSText.ParagraphSm>
                      </WSElement>

                      <WSButton.Link
                        size="S"
                        onClick={() => setShowExtendExpiration(s => !s)}
                      >
                        {showExtendExpiration
                          ? "Cancel extension"
                          : "Extend expiration date"}
                      </WSButton.Link>
                    </WSFlexBox>

                    <WSText.ParagraphSm color="gray600">
                      This requirement has an expiration date extracted from the
                      document used to fulfill the requirement. Review and
                      confirm before marking the requirement complete or extend
                      the expiration date.
                    </WSText.ParagraphSm>
                    <WSElement hidden={!showExtendExpiration}>
                      <WSForm.Field
                        component={WSSelect}
                        componentProps={{
                          options: expirationExtensionOptions
                        }}
                        label="New expiration date"
                        name="extensionType"
                      />
                    </WSElement>
                    <WSInfoBox hidden={!showExtendExpiration}>
                      {extensionType === "custom" && (
                        <WSForm.Field
                          component={WSInputDate}
                          componentProps={{
                            minDate: getStartOfDay(1)
                          }}
                          label="Custom expiration date"
                          mb="L"
                          name="customDate"
                        />
                      )}

                      {nextDate && (
                        <DataItem
                          label="New expiration date"
                          value={toWSDateString(nextDate, "input")}
                        />
                      )}
                    </WSInfoBox>
                  </WSList>
                </WSInfoBox>
              ) : null}

              {isNextDateEarlier ? (
                <WSAlert
                  theme="warning"
                  title="About moving the expiration earlier"
                >
                  You are about to move the expiration to an earlier date than
                  than the original. Please ensure that the necessary
                  communication has been provided to the contractor to avoid
                  unexpected surprises and payment delays.
                </WSAlert>
              ) : isNextDate90DaysLate ? (
                <WSAlert
                  theme="warning"
                  title="New expiration date is over 90 days out"
                >
                  You are about to move the expiration to a future date over 90
                  days out. Please double check the new expiration date.
                  Incomplete requirements may result in payment delays.
                </WSAlert>
              ) : null}

              <WSInfoBox py="L">
                <WSText.ParagraphSm color="gray600">
                  By proceeding, the requirement status will be marked complete
                  by you.
                  <ul>
                    <li>The status will be updated to “Complete”</li>
                  </ul>
                </WSText.ParagraphSm>

                <WSText.ParagraphSm color="gray600">
                  This requirement is assigned to {contractorsLength} of the
                  contractor’s engagements and may affect their eligibility to
                  receive payments from you. Would you like to proceed?
                </WSText.ParagraphSm>
              </WSInfoBox>
              <ErrorAlert
                context="Other"
                error={approveRequirementMeta.isError}
                mt="XL"
              />
            </WSList>
          </WSModal>
        );
      }}
    </WSForm>
  );
};

export const useMarkRequirementAsCompleteModal = () =>
  useWSModalCustom(MarkRequirementAsCompleteModal);
